import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { ProjectDateSortFields, SortDirection, GetProjectDatesQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/projectDate';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useProjectDatesTable = ({ queryOptions }: { queryOptions?: GetProjectDatesQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetProjectDatesQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as ProjectDateSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withProjectDateReminders: true,
                  withProject: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: projectDateData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetProjectDatesQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      projectDateData
        ? { totalCount: projectDateData.projectDates.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [projectDateData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type ProjectDatesBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function ProjectDatesBaseTable(props: ProjectDatesBaseTableProps) {
  const tableProps = useProjectDatesTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
